import { type FC, Fragment } from "react";
import type { AbstractDetail } from "../types";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

const Section: FC<{ label: string; children: string }> = ({
	label,
	children,
}) => (
	<section className="mb-5">
		<h3 className="mb-1.5 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground">
			{label}
		</h3>
		<p className="font-body text-sm leading-[1.7] whitespace-pre-wrap">
			{children}
		</p>
	</section>
);

interface DetailViewProps {
	abstract: AbstractDetail;
	onBack: () => void;
}

export const DetailView: FC<DetailViewProps> = ({ abstract: a, onBack }) => (
	<main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
		<Button variant="ghost" size="sm" className="mb-4" onClick={onBack}>
			&larr; Back to results
		</Button>

		<header className="mb-6">
			<Badge
				variant="secondary"
				className="mb-2 font-mono tabular-nums uppercase tracking-wide"
			>
				Poster {a.number}
			</Badge>
			<h1 className="mb-3 text-xl font-bold leading-tight sm:text-2xl">
				{a.title}
			</h1>
			<div className="mb-6 text-muted-foreground">
				<div className="text-[0.8125rem] italic leading-relaxed">
					{a.authors.map((author, index) => (
						<Fragment key={author.displayName}>
							{index > 0 && ", "}
							{author.displayName}
							<sup>{author.institutionPositions.join(",")}</sup>
						</Fragment>
					))}
				</div>
				<ol className="mt-2 space-y-1 text-xs leading-relaxed">
					{a.institutions.map((institution) => (
						<li key={institution.position}>
							<sup className="mr-1">{institution.position}</sup>
							{institution.text}
						</li>
					))}
				</ol>
			</div>
		</header>

		<Section label="Background / Introduction">{a.background}</Section>
		<Section label="Materials / Methods">{a.methods}</Section>
		<Section label="Results">{a.results}</Section>
		<Section label="Discussion / Conclusion">{a.conclusion}</Section>
	</main>
);
